import React from "react";
import "./TPMS_33.css";

/* ============================================================
   CONTENT  (client ka content – yahin se edit karo)
   wide: true  -> card poori width me aata hai (challenge | solution side by side)
   ============================================================ */
const HEAD = {
  badge: "CHALLENGES & SOLUTIONS",
  title: "Challenges Faced by Modern Toll Plazas",
};

const CHALLENGES = [
  {
    icon: "eyeOff",
    title: "Missed Vehicles and Inaccurate Traffic Counts",
    challenge: [
      "Traditional monitoring systems may fail to record vehicles during heavy traffic, temporary obstruction, low visibility, or system interruptions.",
    ],
    solution: [
      "AI-powered vehicle detection and tracking help identify passing vehicles and maintain accurate lane-wise records. Recorded-video analysis supports recovery and verification of vehicle events when live processing is delayed.",
    ],
  },
  {
    icon: "shield",
    title: "Fraudulent Toll Transactions and Revenue Leakage",
    challenge: [
      "Unauthorized vehicle exemptions, unrecorded transactions, incorrect vehicle categorization, and deliberate manipulation of toll records can contribute to revenue leakage.",
    ],
    solution: [
      "TPMS creates independent video-based vehicle records with associated images, video evidence, lane information, and recording timestamps.",
      "These records can be compared against toll collection data to identify suspicious discrepancies, investigate unauthorized transactions, and discourage fraudulent activities.",
    ],
  },
  {
    icon: "ticket",
    wide: true,
    title: "Excessive or Unauthorized Exempted Vehicles",
    challenge: [
      "A high number of vehicles recorded as exempted can make it difficult for toll operators to distinguish legitimate exemptions from potential misuse.",
      "Without reliable evidence, verifying whether an exemption was justified can become challenging.",
    ],
    solution: [
      "TPMS provides independent evidence of vehicles passing through the toll plaza.",
      "By combining vehicle images, recorded footage, vehicle categories, and passage records with exemption transaction information, operators can investigate:",
    ],
    list: [
      "Unusually high numbers of exempted vehicles",
      "Potentially unauthorized exemptions",
      "Incorrect exemption classifications",
      "Discrepancies between vehicle movements and reported exemptions",
    ],
    after:
      "This creates stronger accountability and helps toll operators control exemption-related revenue leakage.",
  },
  {
    icon: "timer",
    title: "Vehicles Stopping or Moving Slowly at Toll Barriers",
    challenge: [
      "Vehicles frequently stop, wait, or move slowly near toll barriers. Conventional motion-dependent monitoring can lose vehicle continuity or generate duplicate records.",
    ],
    solution: [
      "Intelligent tracking is designed to maintain vehicle identity during slow movement, temporary stops, and subsequent movement, preserving important evidence throughout the transaction.",
    ],
  },
  {
    icon: "truck",
    title: "Incorrect Vehicle Classification",
    challenge: [
      "Misclassification of cars, buses, commercial vehicles, and heavy trucks can affect toll-category verification and operational reporting.",
    ],
    solution: [
      "TPMS uses AI-based vehicle classification to identify different vehicle categories and provide supporting evidence for review of classification discrepancies.",
    ],
  },
  {
    icon: "camera",
    title: "Limited Evidence for Toll Disputes",
    challenge: [
      "Disputes involving vehicle categories, transaction records, exemptions, and unrecorded passages can be difficult to resolve without clear evidence.",
    ],
    solution: [
      "TPMS preserves relevant vehicle images and video evidence with recording timestamps to support transaction verification, investigations, and operational audits.",
    ],
  },
  {
    icon: "wifiOff",
    title: "Camera and Processing Interruptions",
    challenge: [
      "Network instability, processing backlogs, or temporary system failures can affect continuous vehicle monitoring.",
    ],
    solution: [
      "TPMS supports buffered video processing, recovery workflows, and recorded-footage analysis to help prevent the loss of processing results when recorded video remains available.",
    ],
  },
  {
    icon: "dashboard",
    wide: true,
    title: "Lack of Centralized Monitoring",
    challenge: [
      "Managing multiple toll lanes manually makes it difficult to maintain consistent visibility across vehicle movements, camera availability, and transaction activity.",
    ],
    solution: [
      "A centralized monitoring dashboard brings together vehicle records, traffic activity, camera status, and available processing information.",
    ],
  },
];

/* ============================================================
   ICONS
   ============================================================ */
const ICONS = {
  eyeOff: (
    <>
      <path d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49" />
      <path d="M14.084 14.158a3 3 0 0 1-4.242-4.242" />
      <path d="M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143" />
      <path d="m2 2 20 20" />
    </>
  ),
  shield: (
    <>
      <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
      <path d="M12 8v4M12 16h.01" />
    </>
  ),
  ticket: (
    <>
      <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z" />
      <path d="M13 5v2M13 11v2M13 17v2" />
    </>
  ),
  timer: (
    <>
      <path d="M10 2h4" />
      <path d="m12 14 3-3" />
      <circle cx="12" cy="14" r="8" />
    </>
  ),
  truck: (
    <>
      <path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2" />
      <path d="M15 18H9" />
      <path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14" />
      <circle cx="17" cy="18" r="2" />
      <circle cx="7" cy="18" r="2" />
    </>
  ),
  camera: (
    <>
      <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" />
      <circle cx="12" cy="13" r="3" />
    </>
  ),
  wifiOff: (
    <>
      <path d="M12 20h.01" />
      <path d="M8.5 16.429a5 5 0 0 1 7 0" />
      <path d="M5 12.859a10 10 0 0 1 5.17-2.69" />
      <path d="M19 12.859a10 10 0 0 0-2.007-1.523" />
      <path d="M2 8.82a15 15 0 0 1 4.177-2.643" />
      <path d="M22 8.82a15 15 0 0 0-11.288-3.764" />
      <path d="m2 2 20 20" />
    </>
  ),
  dashboard: (
    <>
      <rect x="3" y="3" width="7" height="9" rx="1" />
      <rect x="14" y="3" width="7" height="5" rx="1" />
      <rect x="14" y="12" width="7" height="9" rx="1" />
      <rect x="3" y="16" width="7" height="5" rx="1" />
    </>
  ),
};

const Icon = ({ name, size = 26 }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {ICONS[name]}
  </svg>
);

/* ============================================================
   SECTION
   ============================================================ */
const TPMSChallenges = () => (
  <section id="challenges" className="tc">
    <div className="tc__container">
      <header className="tc__head">
        <span className="tc__pill">{HEAD.badge}</span>
        <h2 className="tc__title">{HEAD.title}</h2>
        <span className="tc__underline" />
      </header>

      <div className="tc__grid">
        {CHALLENGES.map((c, i) => (
          <article
            key={c.title}
            className={`tc__card ${c.wide ? "tc__card--wide" : ""}`}
          >
            <header className="tc__card-head">
              <span className="tc__icon">
                <Icon name={c.icon} />
              </span>
              <div className="tc__card-titles">
                <small>Challenge {String(i + 1).padStart(2, "0")}</small>
                <h3>{c.title}</h3>
              </div>
            </header>

            <div className="tc__body">
              <div className="tc__problem">
                <h4 className="tc__label tc__label--problem">
                  <i aria-hidden="true">!</i> The Challenge
                </h4>
                {c.challenge.map((p) => (
                  <p key={p}>{p}</p>
                ))}
              </div>

              <div className="tc__solution">
                <h4 className="tc__label tc__label--solution">
                  <i aria-hidden="true">✓</i> The TPMS Solution
                </h4>
                {c.solution.map((p) => (
                  <p key={p}>{p}</p>
                ))}

                {c.list && (
                  <ul className="tc__list">
                    {c.list.map((l) => (
                      <li key={l}>{l}</li>
                    ))}
                  </ul>
                )}

                {c.after && <p className="tc__after">{c.after}</p>}
              </div>
            </div>
          </article>
        ))}
      </div>
    </div>
  </section>
);

export default TPMSChallenges;